import React from "react";

import {
  FaMapMarkerAlt,
  FaMedal,
} from "react-icons/fa";

import {
  getEventPalette,
} from "../data/calendarEventColors";

import {
  getEventDateRange,
} from "../utils/calendarUtils";

import {
  getEventPublicStatus,
} from "../utils/calendarViewUtils";

function StatusBadge({ status }) {
  if (!status) return null;

  const classMap = {
    cancelled:
      "border-red-300 bg-red-100 text-red-700 dark:border-red-900 dark:bg-red-950/60 dark:text-red-300",

    postponed:
      "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-300",

    full:
      "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",

    provisional:
      "border-violet-300 bg-violet-100 text-violet-700 dark:border-violet-900 dark:bg-violet-950/60 dark:text-violet-300",
  };

  return (
    <span
      className={[
        "inline-flex rounded-full border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide",
        classMap[status.key] ||
          classMap.full,
      ].join(" ")}
    >
      {status.label}
    </span>
  );
}

export default function CalendarEventPill({
  event,
  dateKey,
  onClick,
}) {
  const palette =
    getEventPalette(event);

  const status =
    getEventPublicStatus(event);

  const { startKey, endKey } =
    getEventDateRange(event);

  const spansMultipleDays =
    startKey &&
    endKey &&
    startKey !== endKey;

  const isFirstDay =
    dateKey === startKey;

  const isLastDay =
    dateKey === endKey;

  let periodLabel = "";

  if (spansMultipleDays) {
    if (isFirstDay) {
      periodLabel = "Début";
    } else if (isLastDay) {
      periodLabel = "Fin";
    } else {
      periodLabel = "Suite";
    }
  }

  const isCancelled =
    status?.key === "cancelled";

  return (
    <button
      type="button"
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onClick?.(event);
      }}
      title={[
        event?.titre,
        event?.discipline_event,
        event?.lieu_event,
        event?.organisateur,
        status?.label,
      ]
        .filter(Boolean)
        .join(" • ")}
      className={[
        "group/event relative w-full overflow-hidden rounded-xl border px-2.5 py-2 text-left",
        "transition duration-200 hover:-translate-y-0.5 hover:shadow-md",
        "border-[var(--calendar-border)]",
        "bg-[var(--calendar-background)]",
        "text-[var(--calendar-text)]",
        "dark:border-[var(--calendar-dark-border)]",
        "dark:bg-[var(--calendar-dark-background)]",
        "dark:text-[var(--calendar-dark-text)]",
        isCancelled
          ? "opacity-65 grayscale"
          : "",
      ].join(" ")}
      style={{
        "--calendar-background":
          palette.lightBackground,

        "--calendar-border":
          palette.lightBorder,

        "--calendar-text":
          palette.lightText,

        "--calendar-dark-background":
          palette.darkBackground,

        "--calendar-dark-border":
          palette.darkBorder,

        "--calendar-dark-text":
          palette.darkText,

        "--calendar-accent":
          palette.accent,
      }}
    >
      <span
        className="absolute bottom-0 left-0 top-0 w-1 bg-[var(--calendar-accent)]"
        aria-hidden="true"
      />

      <span className="block pl-1">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          {periodLabel && (
            <span className="shrink-0 text-[8px] font-black uppercase tracking-wide opacity-65">
              {periodLabel}
            </span>
          )}

          <StatusBadge status={status} />

          {event?.discipline_event && (
            <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-white/55 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide dark:bg-black/20">
              <FaMedal className="shrink-0" />

              <span className="truncate">
                {event.discipline_event}
              </span>
            </span>
          )}
        </span>

        {event?.lieu_event && (
          <span className="mt-1 flex min-w-0 items-center gap-1 text-[9px] font-semibold uppercase tracking-wide opacity-65">
            <FaMapMarkerAlt className="shrink-0 text-[8px]" />

            <span className="truncate">
              {event.lieu_event}
            </span>
          </span>
        )}

        <span
          className={[
            "mt-1 block whitespace-normal break-words text-[12px] font-bold leading-[1.08rem]",
            isCancelled
              ? "line-through"
              : "",
          ].join(" ")}
        >
          {event?.titre ||
            "Événement sans titre"}
        </span>

        {event?.organisateur && (
          <span className="mt-1.5 block truncate text-[9px] font-semibold leading-3 opacity-60">
            {event.organisateur}
          </span>
        )}
      </span>
    </button>
  );
}